import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { callFunction } from '../firebaseLazy';
import { colorForUid } from '../utils/userColors';

// REMOVED 2026-08-10: 'viewAllTasks' and 'editPipelineStages'. Both were
// toggles that granted nothing.
//   • viewAllTasks — no code ever read it. The Tasks page filters to the
//     signed-in user in the Firestore QUERY for every role, so turning it off
//     hid nothing and turning it on revealed nothing.
//   • editPipelineStages — never gated stage editing (the Job Types editor is
//     owner-only). Its single effect was helping decide whether the Settings
//     nav item appeared, which is now managePricingOptions' job — see the
//     settingsVisible note in Sidebar.js.
// Existing user docs keep their stored booleans; they are simply never read,
// written, or displayed again. Deliberately NOT migrated off those docs — a
// stale unused field is harmless, and a bulk rewrite of every user document to
// delete one is risk with no payoff.
// ADDED 2026-08-15: 'showInternalCosts'. It is the inverse of the two removals
// above — a gate that was WRITTEN but never DECLARED. PricingTab.js has always
// read `userPerms?.showInternalCosts !== false` to hide rates, material costs,
// markup, internal totals, the missing-price prompt and the rate-library price
// column. Because the key was absent from this list it was never written to any
// user document and never appeared in the Settings permission editor, so the
// expression was `undefined !== false` for every user in every role: the gate
// could not be closed, and every non-owner role saw the company's cost basis
// on every quote. (Confirmed in the browser as the
// restricted QA account, not inferred from the code.)
//
// Declaring it here changes NOTHING about what anyone sees today — see the
// role defaults below — it makes the switch exist so an owner can actually
// throw it.
// ADDED 2026-09-21: 'viewAllFollowUps'. A SCOPE key, not an access key — it
// does not unlock a page or a field, it widens WHICH RECORDS one existing
// feature reads. Every Stage Checkpoint surface (the record section, the kanban
// dot, the dashboard widget) is rep-scoped without it and company-wide with it;
// see canViewAllFollowUps/canSeeRecordCheckpoint in utils/stageCheckpointUtils.js,
// which is the ONE place that decision is made.
//
// DEFAULT-DENY, and it has to be read that way. getUserPermissions() returns the
// STORED permissions map and never falls back to ROLE_DEFAULT_PERMISSIONS, so
// this key is `undefined` on every user document that already exists — the role
// defaults below only reach a member created after this ships. Read it as
// `!!userPerms?.viewAllFollowUps` (absent → off), NOT as `!== false`, which is
// the inverse convention showInternalCosts deliberately uses. Owner and
// operations never consult it at all: they are covered by MANAGEMENT_ROLES.
export const PERMISSION_KEYS = [
  'viewAnalytics', 'viewFinancials', 'editFinancials', 'deleteRecords',
  'manageTeam', 'exportData', 'importData',
  'accessJobsPage', 'accessServicesPage',
  'editAssignedTasks', 'accessDocumentCruncher', 'managePricingOptions',
  'showInternalCosts', 'viewAllFollowUps',
];

// Plain-language display copy for the permission toggles, keyed by permission.
// DISPLAY ONLY — PERMISSION_KEYS above and everything stored on the user doc are
// untouched; this exists because the Settings toggles were showing owners the
// raw key ("viewAnalytics", "editFinancials"), which asks them to guess what
// they're granting. `label` is what a toggle allows, in a phrase; `desc` is the
// scope/consequence an owner would otherwise have to find out by testing.
//
// Lives here rather than in Settings.js because BOTH the Add Team Member panel
// and the per-member Edit form render this list, and they had drifted — Add was
// printing bare keys while Edit printed title-cased ones.
//
// Every entry here now describes a gate that actually exists. The two that
// described intent rather than enforcement were deleted outright rather than
// reworded — see the note on PERMISSION_KEYS above.
//
// TWO DESCRIPTIONS ARE THE INDUSTRY'S (2026-10-18): managePricingOptions and
// showInternalCosts describe the Pricing tab in a trade's own words, so each
// industry pack carries them (industryCopy().permissionDescs,
// utils/industryRegistry.js) and Settings reads them from there.
export const PERMISSION_LABELS = {
  viewAnalytics: {
    label: 'See company-wide analytics',
    desc: 'Opens the Analytics page — close rates, pipeline velocity, and lead source performance across every rep.',
  },
  viewFinancials: {
    label: 'See job money figures',
    desc: 'Contract values, costs, payments, and profit on jobs and services, plus the Financials page.',
  },
  editFinancials: {
    label: 'Enter and edit money figures',
    desc: 'Add costs and record payments on a job. Only useful alongside “See job money figures”.',
  },
  deleteRecords: {
    label: 'Delete clients, jobs, and services',
    desc: 'Permanent — deleted records are not recoverable from the app.',
  },
  manageTeam: {
    label: 'Add and manage team members',
    desc: 'Invite people, change their role and permissions, and reset passwords. Also unlocks Settings.',
  },
  exportData: {
    label: 'Export data to spreadsheets',
    desc: 'Downloads client, job, and service lists as CSV. Also unlocks Settings.',
  },
  importData: {
    label: 'Import records from spreadsheets',
    desc: 'Bulk-adds clients and jobs from a CSV file. Also unlocks Settings.',
  },
  accessJobsPage: {
    label: 'Open the Workflows job board',
    desc: 'Without this and Services below, the Workflows item disappears from the sidebar.',
  },
  accessServicesPage: {
    label: 'Open the Workflows service board',
    desc: 'The service-ticket half of Workflows, separate from jobs.',
  },
  editAssignedTasks: {
    label: 'Edit tasks assigned to them',
    desc: 'Change the title, due date, or notes on their own tasks. Completing a task never needs this.',
  },
  accessDocumentCruncher: {
    label: 'Use the Document Cruncher',
    desc: 'Run plan and document extraction. Uses the company’s monthly Cruncher allowance.',
  },
  // desc: the industry's (see above).
  managePricingOptions: {
    label: 'Manage shared pricing options',
  },
  // desc: the industry's (see above).
  showInternalCosts: {
    label: 'See cost and markup on quotes',
  },
  viewAllFollowUps: {
    label: 'See the whole team’s follow-ups',
    desc: 'Without this they see Follow Up only on jobs assigned to them. With it they get the Needs Follow-Up list for every rep, filterable by rep.',
  },
};

// Display label for a permission key, falling back to the raw key so a newly
// added permission is still readable before its copy is written.
export function getPermissionLabel(key) {
  return (PERMISSION_LABELS[key] && PERMISSION_LABELS[key].label) || key;
}

export function getPermissionDesc(key) {
  return (PERMISSION_LABELS[key] && PERMISSION_LABELS[key].desc) || null;
}

const OWNER_PERMISSIONS = Object.fromEntries(PERMISSION_KEYS.map(k => [k, true]));

// managePricingOptions gates editing the General Info dropdown lists (frame
// type, glass, trim…) that every quote inherits — the on-page gear on the
// Pricing tab. Deliberately its own granular key rather than a role check:
// it's a shared-vocabulary edit that affects every future quote, so the people
// who run the pricing (owner, operations) get it and a rep quoting a
// house does not — but any owner can hand it to a specific rep.
//
// showInternalCosts: the product call flagged in BONEFLOW_VISION.md's audit
// section has now been made — technician defaults to FALSE, every other role
// stays true. This is a DEFAULT ONLY, and it reaches exactly one
// place: `getDefaultPermissions(role)`, which is read when a member record is
// first created. Existing users are untouched — `getUserPermissions` returns
// the stored `userProfile.permissions` and never falls back to this table, so
// a technician already in the system keeps whatever they have (including the
// no-stored-key case, which still evaluates `undefined !== false` as visible).
// Turning it back on for a specific person is a per-user toggle in Settings.
//
// viewAllFollowUps: owner (via OWNER_PERMISSIONS) and operations true, salesman
// and technician false — the same shape managePricingOptions carries, and for
// the same reason. Follow Up is a rep's own worklist by default; seeing the
// whole team's is a management view an owner can hand to a specific person (a
// lead rep covering a territory) without promoting them. Like every entry in
// this table it is a DEFAULT ONLY and reaches nothing but a newly created
// member record.
const ROLE_DEFAULT_PERMISSIONS = {
  owner: OWNER_PERMISSIONS,
  operations: {
    viewAnalytics: true, viewFinancials: true, editFinancials: true,
    accessJobsPage: true, accessServicesPage: true,
    deleteRecords: true, exportData: true,
    accessDocumentCruncher: true, managePricingOptions: true,
    showInternalCosts: true, viewAllFollowUps: true,
    manageTeam: false, importData: false, editAssignedTasks: false,
  },
  salesman: {
    accessJobsPage: true, viewFinancials: true, accessDocumentCruncher: true,
    showInternalCosts: true,
    viewAnalytics: false, editFinancials: false, deleteRecords: false,
    manageTeam: false, exportData: false, importData: false,
    accessServicesPage: false,
    editAssignedTasks: false, managePricingOptions: false,
    viewAllFollowUps: false,
  },
  technician: {
    accessJobsPage: true, showInternalCosts: false,
    viewAnalytics: false, viewFinancials: false, editFinancials: false,
    deleteRecords: false, manageTeam: false, exportData: false,
    importData: false,
    accessServicesPage: false, editAssignedTasks: false,
    accessDocumentCruncher: false, managePricingOptions: false,
    viewAllFollowUps: false,
  },
};

// ── FOUR ROLES, AND TWO STORED VALUES THAT ARE NOT ROLES (2026-09-16) ────────
//
// There used to be six entries above. Two of them were duplicates: `manager`
// was byte-identical to `operations`, `installer` byte-identical to
// `technician` — verified key by key, including the keys each map leaves
// ABSENT, not assumed from the labels. They are gone, and the user documents
// carrying them were migrated (scripts/migrateRoles.js). Because the maps were
// identical, that migration could not change what anyone can do; it is a rename
// of a stored string.
//
// THESE ALIASES ARE NOT A SECOND ROLE LIST. They exist so a stored value that
// predates the migration — an un-migrated environment, a document written by an
// old client, a restore from backup — still resolves to a real role instead of
// falling through to the salesman default and silently handing a technician
// financials. Everything that reads a role goes through normalizeRole() below,
// the same way every stage-category read goes through normalizeCategory().
// Nothing WRITES an alias, and nothing offers one.
const LEGACY_ROLE_ALIASES = {
  manager: 'operations',
  installer: 'technician',
};

/**
 * The canonical role id for any stored value. Legacy aliases resolve to the
 * role that survived; anything already canonical passes through; anything
 * unrecognised is returned untouched so a caller can still decide what to do
 * with it (getRoleLabel prints it, getDefaultPermissions falls back).
 */
export function normalizeRole(role) {
  return LEGACY_ROLE_ALIASES[role] || role;
}

export const ROLE_LABELS = {
  owner: 'Owner',
  operations: 'Operations',
  salesman: 'Salesman',
  technician: 'Field Technician',
};

// Roles offered when creating a new team member. The ONE list — Settings' Add
// Team Member and per-member Edit dropdowns, guided onboarding's first team
// member, and the notification "By role" picker all read it rather than keeping
// a copy (the copies had already drifted before this list became canonical).
export const ROLE_OPTIONS = [
  { value: 'owner', label: ROLE_LABELS.owner },
  { value: 'operations', label: ROLE_LABELS.operations },
  { value: 'salesman', label: ROLE_LABELS.salesman },
  { value: 'technician', label: ROLE_LABELS.technician },
];

/**
 * Every value a role may be STORED as, canonical first — the inverse of
 * LEGACY_ROLE_ALIASES, grouped. Anything that has to MATCH stored role strings
 * rather than normalize one (a Firestore `in` query, a notification recipient
 * rule) needs the whole set, and deriving it here is what stops a second,
 * hand-kept copy drifting the way the last one did.
 *
 *   { owner: ['owner'], operations: ['operations','manager'],
 *     salesman: ['salesman'], technician: ['technician','installer'] }
 */
export const ROLE_STORED_VALUES = Object.fromEntries(
  Object.keys(ROLE_LABELS).map(canonical => [
    canonical,
    [canonical, ...Object.keys(LEGACY_ROLE_ALIASES).filter(a => LEGACY_ROLE_ALIASES[a] === canonical)],
  ]),
);

// ── ROLE SETS, so a capability check is never an inline array again ──────────
//
// Twelve call sites across six files each carried their own literal list of
// role strings, and they had drifted: every one of them spelled out 'manager'
// beside 'operations' (correct, if verbose) and NOT ONE of them listed
// 'installer' beside 'technician' — so an installer could not be picked as a
// task assignee while a technician with the identical permission map could.
// Naming the sets here fixes that by construction, and roleIn() normalizes, so
// a legacy stored value lands in the same set its surviving role does.
//
// TASK_ASSIGNEE_ROLES currently spans every role. That is deliberate rather
// than redundant: it says "anyone with a recognised role can be handed work",
// which is a product statement that would otherwise be invisible, and it is the
// filter that keeps an unknown/blank role out of an assignee dropdown.
export const TASK_ASSIGNEE_ROLES = ['owner', 'operations', 'salesman', 'technician'];
// Who can carry a client or a job as the responsible rep. Field technicians are
// excluded: they work jobs, they do not own the relationship.
export const SALES_ROLES = ['owner', 'operations', 'salesman'];
// Who sees the business rather than their own slice of it.
export const MANAGEMENT_ROLES = ['owner', 'operations'];

/** Membership test for the sets above, tolerant of legacy stored values. */
export function roleIn(role, roles) {
  return roles.includes(normalizeRole(role));
}
/**
 * WHO "MANAGEMENT" IS, when something has to be escalated to a person.
 *
 *   { primary: {uid, name} | null, all: [{uid, name}], names: [string] }
 *
 * THE CONVENTION THIS FOLLOWS is the one functions/notificationRouting.js has
 * used since notifications shipped: the events that mean "management should know"
 * (problemJobFlagged, fieldServiceSubmitted) route to
 * `[{ type: 'owner' }, { type: 'role', role: 'operations' }]` — the ONE company
 * owner plus EVERY operations user. `all` is exactly that set.
 *
 * WHY THERE IS ALSO A `primary`, AND WHY IT IS THE OWNER. A task has one
 * delivering assignee: every feed that puts a task in front of somebody queries
 * `where('assignedTo','==',uid)`. Fanning one escalation out as N tasks for N
 * managers would produce N duplicates that nobody can close as a group, so the
 * caller assigns to `primary` and records `all` on assignedToIds/assignedToNames
 * — precisely what createAutomationTask already does. The array-contains fan-out
 * that shape anticipates then lights up every manager later with no migration.
 *
 * RESOLUTION ORDER for the owner, mirroring notificationRouting.js: the company
 * doc's own ownerUid first, since that is its record of who owns it, then a scan
 * for a role === 'owner' member for a company doc that predates the field. If
 * neither answers, the first operations user stands in — an escalation with
 * nowhere to go is worse than one that goes to the next person up, and returning
 * null primary would silently drop the task.
 */
export function resolveManagementRecipients(company, companyUsers) {
  const users = Array.isArray(companyUsers) ? companyUsers.filter(Boolean) : [];
  const nameOf = (u) => {
    const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return full || u.email || '';
  };

  const ownerUid = company?.ownerUid || null;
  const owner = (ownerUid && users.find(u => u.uid === ownerUid))
    || users.find(u => normalizeRole(u.role) === 'owner')
    || null;

  const operations = users.filter(u => normalizeRole(u.role) === 'operations');

  const all = [];
  const seen = new Set();
  for (const u of [owner, ...operations]) {
    if (!u || !u.uid || seen.has(u.uid)) continue;
    seen.add(u.uid);
    all.push({ uid: u.uid, name: nameOf(u) });
  }

  return { primary: all[0] || null, all, names: all.map(r => r.name) };
}


export function getRoleLabel(role) {
  return ROLE_LABELS[normalizeRole(role)] || role || 'Member';
}

export function getDefaultPermissions(role) {
  return ROLE_DEFAULT_PERMISSIONS[normalizeRole(role)] || ROLE_DEFAULT_PERMISSIONS.salesman;
}

export function getUserPermissions(userProfile) {
  if (!userProfile) return {};
  if (userProfile.role === 'owner') return OWNER_PERMISSIONS;
  return userProfile.permissions || {};
}

export async function getUserProfile(uid) {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data();
  }
  return null;
}

export async function createUserProfile(uid, email, role = 'salesman', companyId = null) {
  const userRef = doc(db, 'users', uid);
  const userData = {
    uid,
    email,
    role,
    companyId,
    name: '',
    permissions: getDefaultPermissions(role),
    userColor: colorForUid(uid),
    createdAt: new Date(),
  };
  await setDoc(userRef, userData);
  return userData;
}

// Self-serve signup (/signup): the full owner user record, written AFTER the
// company exists. A deliberate full setDoc (not update) — it overwrites the
// minimal stub AuthContext auto-creates on first sign-in (role 'salesman',
// companyId null) with the real owner profile.
export async function createOwnerUserProfile(uid, { email, firstName, lastName, phone, companyId }) {
  const userRef = doc(db, 'users', uid);
  const userData = {
    uid,
    email,
    firstName,
    lastName,
    ...(phone ? { phone } : {}),
    companyId,
    role: 'owner',
    permissions: getDefaultPermissions('owner'),
    userColor: colorForUid(uid),
    // Default personal accent pair for new accounts: Rich Imperial Gold fill
    // with near-black text (the platform default). ThemeContext falls back to
    // these same values when unset, so this simply makes the default explicit
    // on the owner's record at creation time.
    accentColor: '#b8860b',
    accentTextColor: '#1a1a1a',
    createdAt: new Date(),
    notificationPreferences: { taskAssigned: true, stageChanged: false },
  };
  await setDoc(userRef, userData);
  return userData;
}

// Poll users/{uid} until the doc exists (or the timeout passes). The signup
// flow waits on this after creating the Firebase Auth user: AuthContext's
// profile listener auto-creates a stub doc on first sign-in, and waiting for
// that write to land guarantees the owner profile written next is the LAST
// write — the stub can never arrive late and clobber it. Timing out is
// non-fatal (the owner setDoc simply creates the doc itself).
//
// NEVER THROWS — and that is load-bearing, not defensive habit. This is the
// FIRST Firestore read of a brand-new session, fired the instant the auth user
// exists, so it is the single most likely call in the whole signup flow to hit
// a cold/blocked connection: a captive-portal or carrier proxy, a mobile
// content blocker that filters firestore.googleapis.com, or simply a dropped
// packet on cellular. Before this guard existed, any of those rejected the
// promise and killed a signup whose auth user had ALREADY been created — and
// the retry path reused that same auth user and failed in exactly the same
// place, so the account was permanently stranded behind a generic error. A
// failed read is treated exactly like "not there yet": keep polling, and let
// the timeout fall through to the non-fatal path the doc comment promises.
export async function waitForUserProfile(uid, { timeoutMs = 6000, intervalMs = 250 } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const profile = await getUserProfile(uid);
      if (profile) return profile;
    } catch (err) {
      // Log once per attempt rather than swallowing silently — if signup is
      // failing on a device, this is the breadcrumb that names the reason.
      console.warn('waitForUserProfile read failed (retrying):', err?.code || err?.message || err);
    }
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  return null;
}

export async function getCompanyUsers(companyId) {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('companyId', '==', companyId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Real-time subscription to all users in a company. The callback receives the
// full list whenever any user changes. Returns the unsubscribe handle.
export function subscribeToCompanyUsers(companyId, callback) {
  if (!companyId) return () => {};
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('companyId', '==', companyId));
  return onSnapshot(q, (snapshot) => {
    const users = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(users);
  }, (err) => {
    console.error('subscribeToCompanyUsers failed:', err);
    callback([]);
  });
}

export async function updateUserProfile(uid, data) {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { ...data });
}

// ── Cloud Function callables ────────────────────────────────────────────────
//
// AuthContext imports this module on the first tick, so a module-scope
// `getFunctions(app)` here was what dragged the Cloud Functions SDK into the
// main chunk for every visitor. `callFunction` loads it on first call instead.

export async function createCompanyUserCallable(params) {
  return callFunction('createCompanyUser', params, { timeout: 60000 });
}

export async function resetUserPasswordCallable(params) {
  return callFunction('resetUserPassword', params, { timeout: 30000 });
}

export async function removeCompanyUserCallable(params) {
  return callFunction('removeCompanyUser', params, { timeout: 30000 });
}

export async function updateUserEmailCallable(params) {
  return callFunction('updateUserEmail', params, { timeout: 30000 });
}

// Self-serve signup welcome email. The function reads the caller's own user +
// company docs, so no params are needed. Callers should fire-and-forget with a
// .catch — a failed welcome email must never block signup.
export async function sendSignupWelcomeEmailCallable() {
  return callFunction('sendSignupWelcomeEmail', {}, { timeout: 30000 });
}
